import { vellumApiSdk } from "@vellum/micro-app-auth";
import {
  getFriendlyDate,
  RecordTable,
  usePaginatedQuery,
  type Column,
} from "@vellum/ui";
import type {
  AdminCompanySchema,
  AdminFacilitySchema,
} from "@vellum/vellum-api";
import { useState, type ChangeEvent } from "react";

import { ListPage } from "@/components/ListPage";
import { ADMIN_PAGE_SIZE } from "@/lib/pagination";

const ALL_COMPANIES = "";
/** Company filter dropdown — enough for the select, not a paged table. */
const COMPANY_DROPDOWN_LIMIT = 100;

const COLUMNS: Column<AdminFacilitySchema>[] = [
  { header: "Name", render: (facility) => facility.name },
  { header: "Company", render: (facility) => facility.company?.name ?? "—" },
  {
    header: "Location",
    render: (facility) => `${facility.city}, ${facility.state}`,
    className: "hidden sm:table-cell",
  },
  {
    header: "Timezone",
    render: (facility) => facility.timezone,
    className: "hidden lg:table-cell",
  },
  {
    header: "Created",
    render: (facility) => getFriendlyDate(facility.created_at),
    className: "hidden md:table-cell",
  },
];

export function FacilitiesPage() {
  const [query, setQuery] = useState("");
  const [companyId, setCompanyId] = useState(ALL_COMPANIES);
  const [page, setPage] = useState(0);
  const needle = query.trim();

  const companies = usePaginatedQuery<AdminCompanySchema>(
    () =>
      vellumApiSdk.adminListCompanies({
        query: { limit: COMPANY_DROPDOWN_LIMIT, offset: 0 },
      }),
    "Something went wrong loading companies.",
  );

  const facilities = usePaginatedQuery<AdminFacilitySchema>(
    () =>
      vellumApiSdk.adminListFacilities({
        query: {
          limit: ADMIN_PAGE_SIZE,
          offset: page * ADMIN_PAGE_SIZE,
          q: needle || undefined,
          company_id: companyId || undefined,
        },
      }),
    "Something went wrong loading facilities.",
    [page, needle, companyId],
  );

  return (
    <ListPage
      title="Facilities"
      subtitle="Every facility in Luna"
      query={query}
      onQueryChange={(value) => {
        setQuery(value);
        setPage(0);
      }}
      searchPlaceholder="Facility or city"
      filters={
        <label className="w-full sm:max-w-xs">
          <span className="mb-1 block text-sm font-medium text-gray-700">
            Company
          </span>
          <select
            value={companyId}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              setCompanyId(event.target.value);
              setPage(0);
            }}
            className="h-11 w-full rounded-md border-0 bg-white px-2 text-sm text-gray-900 ring-1 ring-gray-300 ring-inset focus:ring-2 focus:ring-inset"
          >
            <option value={ALL_COMPANIES}>All companies</option>
            {companies.items.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </label>
      }
      loading={facilities.loading}
      isFetching={facilities.isFetching}
      error={facilities.error ?? companies.error}
      page={page}
      pageSize={ADMIN_PAGE_SIZE}
      total={facilities.total}
      onPageChange={setPage}
    >
      <RecordTable
        columns={COLUMNS}
        rows={facilities.items}
        rowKey={(facility) => facility.id}
        emptyMessage={
          needle || companyId
            ? "No facilities match those filters."
            : "No facilities yet."
        }
      />
    </ListPage>
  );
}
